import React, { PropsWithChildren, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useFormikContext } from 'formik'

import { Button } from '../../../global/button/Button'
import { useArabicSessionLanguage } from '../../../global/context/SessionSettingsContext'
import { DepositAlertIcon } from '../../../icons/DepositAlertIcon'
import { ExternalLinkIcon } from '../../../icons/ExternalLinkIcon'
import { CurrencyType } from '../../../model/WalletDto'
import { TextH3, TextSmall } from '../../../ui/Typography/Typography'
import { getCurrencySymbol } from '../../../utils/transaction.utils'
import { TradingAccountDepositFormValues } from '../../Trading-Accounts/TradingAccountDeposit/TradingAccountDepositForm/TradingAccountDepositForm'
import { WalletDepositFormValues } from '../../Wallets/WalletDeposit/WalletDepositForm/WalletDepositForm'

import styles from './CryptoProceedModal.module.scss'

interface CryptoProceedModalProps {
  title?: string
  onProceed(): void
  onCancel?(): void
}

type FormValues = WalletDepositFormValues | TradingAccountDepositFormValues

const isWalletDepositFormValues = (values: FormValues): values is WalletDepositFormValues =>
  (values as WalletDepositFormValues).wallet !== undefined

const isTradingAccountDepositFormValues = (
  values: FormValues
): values is TradingAccountDepositFormValues =>
  (values as TradingAccountDepositFormValues).tradingAccount !== undefined

export const CryptoProceedModal: React.FunctionComponent<
  PropsWithChildren<CryptoProceedModalProps>
> = ({ title, onProceed }) => {
  const { values } = useFormikContext<FormValues>()
  const { t } = useTranslation()

  const isArabic = useArabicSessionLanguage()

  const [currency, setCurrency] = useState<CurrencyType | undefined>(undefined)

  useEffect(() => {
    if (isWalletDepositFormValues(values)) {
      setCurrency(values.wallet.currency.id as CurrencyType)
    } else if (isTradingAccountDepositFormValues(values)) {
      setCurrency(values.tradingAccount.currency.id as CurrencyType)
    }
  }, [values])

  return (
    <section className={styles.body}>
      <div className={styles.content}>
        <DepositAlertIcon />
        <div className={styles.depositInfo}>
          <TextH3 className={styles.hasExtraMarginBottom}>{`${title} ${getCurrencySymbol(
            currency as CurrencyType
          )}${values.amount}`}</TextH3>
          <TextSmall isParagraph className={styles.hasExtraMarginBottom}>
            {t('Please note that third party transfers are not accepted for funding.')}
          </TextSmall>
          <TextSmall isParagraph className={styles.hasExtraMarginBottom}>
            {t(
              'All deposits and withdrawals in Cryptocurrencies must be made every time directly in Client Area'
            )}
          </TextSmall>
          <TextSmall isParagraph>
            {t(
              'Please note that Crypto Address issued by crypto provider is unique for each transaction'
            )}
          </TextSmall>
        </div>

        <Button
          appearance='primary'
          className={styles.button}
          size='M'
          fullWidth
          type='submit'
          onClick={onProceed}
          renderRightIcon={() => (
            <span className={classNames(styles.externalIcon, { [styles.iconFlipped]: isArabic })}>
              <ExternalLinkIcon themable size={14} />
            </span>
          )}
        >
          {t('Proceed')}
        </Button>
      </div>
    </section>
  )
}
